/**
 * PersonalDataSection Component
 *
 * Main personal data section for the redesigned settings page.
 * Features auto-saving display name, avatar management, and email display.
 *
 * @module pages/SettingsPage/components/PersonalDataSection
 */

import {
  type ChangeEvent,
  type ReactElement,
  memo,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { Stack, TextInput } from '@mantine/core';

import { usersService } from '@/services/api';
import { NotificationService } from '@/services/notificationService';
import { useGeoMarkupStore } from '@/store';

import { AutoSaveIndicator } from './AutoSaveIndicator';
import { AvatarEditor } from './AvatarEditor';
import { DeleteAccountSection } from './DeleteAccountSection';
import styles from './PersonalDataSection.module.css';

/** Debounce delay for auto-saving display name (milliseconds) */
const DISPLAY_NAME_DEBOUNCE_MS = 800;

/** Duration to show "saved" status before returning to idle (milliseconds) */
const SAVED_STATUS_DISPLAY_MS = 2000;

type AutoSaveStatus = 'idle' | 'saving' | 'saved' | 'error';

interface PersonalDataSectionProps {
  displayName: string;
  email: string;
  avatarUrl: string | null;
  onNavigateToEmail: () => void;
}

export const PersonalDataSection = memo(function PersonalDataSection({
  displayName,
  email,
  avatarUrl,
  onNavigateToEmail,
}: PersonalDataSectionProps): ReactElement {
  const { t } = useTranslation();

  const updateUserProfile = useGeoMarkupStore(
    (state) => state.auth.updateUserProfile
  );

  const [localDisplayName, setLocalDisplayName] = useState(displayName);
  const [autoSaveStatus, setAutoSaveStatus] = useState<AutoSaveStatus>('idle');
  const [isUploading, setIsUploading] = useState(false);

  const debounceRef = useRef<ReturnType<typeof setTimeout> | undefined>(
    undefined
  );
  const savedTimeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(
    undefined
  );

  useEffect(() => {
    return () => {
      clearTimeout(debounceRef.current);
      clearTimeout(savedTimeoutRef.current);
    };
  }, []);

  const debouncedSaveDisplayName = useCallback(
    async (value: string) => {
      const trimmed = value.trim();
      if (!trimmed || trimmed === displayName) return;

      setAutoSaveStatus('saving');
      try {
        await usersService.updateProfile({ displayName: trimmed });
        updateUserProfile({ displayName: trimmed });
        setAutoSaveStatus('saved');

        clearTimeout(savedTimeoutRef.current);
        savedTimeoutRef.current = setTimeout(
          () => setAutoSaveStatus('idle'),
          SAVED_STATUS_DISPLAY_MS
        );
      } catch (error: unknown) {
        console.error('Failed to auto-save display name:', error);
        setAutoSaveStatus('error');
      }
    },
    [displayName, updateUserProfile]
  );

  const handleDisplayNameChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const value = event.currentTarget.value;
      setLocalDisplayName(value);

      clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(
        () => void debouncedSaveDisplayName(value),
        DISPLAY_NAME_DEBOUNCE_MS
      );
    },
    [debouncedSaveDisplayName]
  );

  const handleAvatarUpload = useCallback(
    async (file: File) => {
      setIsUploading(true);
      try {
        const response = await usersService.uploadAvatar(file);
        updateUserProfile({ avatarUrl: response.data.avatarUrl });
      } catch (error: unknown) {
        console.error('Failed to upload avatar:', error);
        NotificationService.showError(t('settings.profile.avatarUploadError'));
      } finally {
        setIsUploading(false);
      }
    },
    [updateUserProfile, t]
  );

  const handleAvatarDelete = useCallback(async () => {
    try {
      await usersService.deleteAvatar();
      updateUserProfile({ avatarUrl: null });
    } catch (error: unknown) {
      console.error('Failed to delete avatar:', error);
      NotificationService.showError(t('settings.profile.avatarDeleteError'));
    }
  }, [updateUserProfile, t]);

  return (
    <div className={styles.section}>
      <div className={styles.sectionHeader}>
        <h2 className={styles.title}>{t('settings.profile.sectionTitle')}</h2>
        <AutoSaveIndicator status={autoSaveStatus} />
      </div>

      <hr className={styles.divider} />

      <Stack gap="lg">
        <AvatarEditor
          avatarUrl={avatarUrl}
          displayName={displayName}
          onUpload={handleAvatarUpload}
          onDelete={handleAvatarDelete}
          isUploading={isUploading}
        />

        <TextInput
          label={t('settings.profile.displayName')}
          value={localDisplayName}
          onChange={handleDisplayNameChange}
        />

        <div>
          <TextInput
            label={t('settings.profile.emailLabel')}
            value={email}
            readOnly
            classNames={{ input: styles.emailInput }}
          />
          <button
            type="button"
            className={styles.emailLink}
            onClick={onNavigateToEmail}
          >
            {t('settings.profile.changeEmail')}
          </button>
        </div>
      </Stack>

      <hr className={styles.deleteAccountDivider} />
      <DeleteAccountSection />
    </div>
  );
});
